/**
 * In-memory stand-in for the `quality` API, used until the backend ships.
 *
 * Implements the same contract as qualityApi.js — same method names, same
 * response envelope, same validation messages — so switching to the real API
 * is a matter of turning the mock off, not rewriting screens. All data below is
 * sample data and resets on page reload.
 *
 * Deliberately has no imports from the rest of the module, to keep the
 * qualityApi -> qualityMock dependency one-directional.
 */

const STAGE_KEYS = ["raised", "classified", "traced", "rca", "fix_in_progress", "regression", "qa_approved", "released", "verified", "closed"];
const STAGE_LABELS = ["Raised", "Classified", "Traced", "Root cause", "Fix in progress", "Regression", "QA approved", "Released", "Verified", "Closed"];
const ME = (() => {
    try {
        return JSON.parse(localStorage.getItem("loginData"))?.data?.user?.member?.name || "You";
    } catch {
        return "You";
    }
})();
const nowIso = () => new Date().toISOString();
const today = () => new Date().toISOString().slice(0, 10);

const SUBS = {
    Pharmacy: ["Billing", "Sales Return", "Bill Cancel", "Purchase Inward / GRN", "Purchase Request"],
    Laboratory: ["Sample Collection", "Result Print", "Culture Report", "Outsource"],
    Billing: ["IP Settlement", "Service Master", "OP Billing"],
    "Front Office": ["Registration", "Patient Search", "Hospital Configuration"],
    Nursing: ["Nurse Notes", "Vitals"],
    Radiology: ["Billing", "Reporting"],
};
const MEMBERS = ["Arun K", "Divya R", "Karthik M", "Sneha P", "Meena T", "Vignesh S", "Priya N", "Ravi Shankar"];
const ORGS = ["Koyili Hospital", "ANNAI Medical Centre", "Gowsbai Hospital", "ARS Hospital", "EVP Hospital", "FIMS"];
const SERVERS = [{ code: "prod", label: "eMedHub Koyili" }, { code: "default", label: "eMedHub UAT" }];
const serverLabel = (code) => (SERVERS.find((s) => s.code === code) || {}).label || code;

/* ── test cases ─────────────────────────────────────────────────────────── */
const OWNER = { Pharmacy: "Divya R", Laboratory: "Priya N", Billing: "Sneha P", "Front Office": "Priya N", Nursing: "Priya N", Radiology: "Sneha P" };
let tcSeq = 0;
const tc = (code, title, module, sub, extra = {}) => {
    tcSeq += 1;
    return {
        id: tcSeq, code, title, module, sub_module: sub,
        case_type: "Positive", priority: extra.critical ? "High" : "Medium",
        in_regression: true, critical: false, status: "Active",
        preconditions: "", test_data: "", applies_to: "all", setting_name: "",
        owner: OWNER[module], tags: "", linked_bugs: [],
        steps: [{ action: `Follow the scenario: ${title.toLowerCase()}`, expected: "Works as described in the title" }],
        ...extra,
    };
};
const TEST_CASES = [
    tc("TC-0012", "Pending amount shown on pharmacy billing tab", "Pharmacy", "Billing", {
        priority: "High", tags: "billing-critical", test_data: "UHID 10045 · bill PH-2291",
        preconditions: "Patient has one pharmacy bill with part of it unpaid.",
        steps: [{ action: "Open Pharmacy → Billing for the patient", expected: "Billing tab loads with the bill listed" },
                { action: "Read the Pending amount field", expected: "Shows the unpaid part of the bill" }],
    }),
    tc("TC-0013", "Pending amount after a partial sales return", "Pharmacy", "Sales Return", {
        case_type: "Negative", priority: "High", preconditions: "Bill of 5 items, fully unpaid.", test_data: "UHID 10045 · bill PH-2292",
        steps: [{ action: "Return 2 items from the bill", expected: "Return saved, credit note created" },
                { action: "Open the Billing tab", expected: "Pending amount = bill total − returned items" }],
    }),
    tc("TC-0014", "Cancel bill limited to admin within the time window", "Pharmacy", "Bill Cancel", {
        critical: true, priority: "High", preconditions: "Bill-cancel window set to 24 hours. Logged in as admin.",
        applies_to: "setting", setting_name: "Bill cancel window enabled",
        steps: [{ action: "Create a pharmacy bill", expected: "Bill saved" }, { action: "Cancel it within 1 hour", expected: "Bill cancelled and stock returned" }],
    }),
    tc("TC-0021", "GRN entry with free quantity and scheme discount", "Pharmacy", "Purchase Inward / GRN"),
    tc("TC-0022", "Edit an individual purchase request", "Pharmacy", "Purchase Request", { in_regression: false }),
    tc("TC-0031", "Specimen collected time saved on the sample", "Laboratory", "Sample Collection"),
    tc("TC-0032", "Ordered date shown on lab result print", "Laboratory", "Result Print"),
    tc("TC-0033", "Culture report prints efficacy ratio", "Laboratory", "Culture Report"),
    tc("TC-0041", "Register patient with mandatory fields", "Front Office", "Registration", { critical: true, priority: "High" }),
    tc("TC-0042", "Patient search by UHID and mobile", "Front Office", "Patient Search", { critical: true, priority: "High", case_type: "Negative" }),
    tc("TC-0043", "Nurse configuration saved per ward", "Front Office", "Hospital Configuration", { in_regression: false, status: "Draft" }),
    tc("TC-0051", "IP bill final settlement with advance", "Billing", "IP Settlement", { critical: true, priority: "High" }),
    tc("TC-0052", "Service master available in all modules", "Billing", "Service Master"),
    tc("TC-0061", "Vitals in nurse notes update patient status", "Nursing", "Nurse Notes"),
];
const tcByCode = (code) => TEST_CASES.find((t) => t.code === code);
const TICKET_LINKS = {}; // ticket id -> [TC codes], for the sprint ticket tab

// Current sprint links, so "Linked tickets" in the library is realistic.
const SPRINT_LINKS = {
    "S 52-01": ["TC-0012", "TC-0013"], "S 52-02": ["TC-0052"], "S 52-03": ["TC-0031", "TC-0032"],
    "S 52-04": ["TC-0014"], "S 52-07": ["TC-0021"], "S 52-09": ["TC-0022"], "S 52-10": ["TC-0061"], "S 52-12": ["TC-0033"],
};

/* ── releases ───────────────────────────────────────────────────────────── */
let exSeq = 0;
const ex = (code, result, tester = "", at = "", comment = "", waiver = "") => {
    exSeq += 1;
    return { id: exSeq, tc_code: code, result, tester, executed_at: at, comment, waiver_reason: waiver };
};
const tk = (code, description, module, sprint, developers, qa, peer_qa, uat, approver, test_cases) =>
    ({ code, description, module, sprint, developers, qa, peer_qa, uat, approver, test_cases });

const RELEASES = [
    {
        id: 1, version: "v-4.18.0", release_date: "2026-09-08", status: "deployed", dev_bugs: 14,
        servers: [{ code: "prod", version: "v-4.18.0" }, { code: "default", version: "v-4.18.0" }],
        tickets: [
            tk("S 49-02", "Pharmacy Bills - add Cancel Bill (admin only, configurable window)", "Pharmacy", "Sprint 49", ["Karthik M"], "Divya R", "", "Meena T", "Ravi Shankar", ["TC-0014"]),
            tk("S 49-05", "All Modules >> Service Master available in every module", "Billing", "Sprint 49", ["Arun K"], "Sneha P", "Priya N", "Meena T", "Ravi Shankar", ["TC-0052"]),
            tk("S 49-09", "Vitals in Nurse Notes update the patient status", "Nursing", "Sprint 49", ["Vignesh S"], "Priya N", "", "Meena T", "Ravi Shankar", ["TC-0061"]),
        ],
        executions: [ex("TC-0014", "pass", "Divya R", "2026-09-05"), ex("TC-0041", "pass", "Priya N", "2026-09-05"), ex("TC-0042", "pass", "Priya N", "2026-09-05"),
            ex("TC-0051", "pass", "Sneha P", "2026-09-06"), ex("TC-0052", "pass", "Sneha P", "2026-09-06"), ex("TC-0061", "pass", "Priya N", "2026-09-06")],
    },
    {
        id: 2, version: "v-4.18.5", release_date: "2026-09-29", status: "deployed", dev_bugs: 9,
        servers: [{ code: "prod", version: "v-4.18.5" }, { code: "default", version: "v-4.18.6" }],
        tickets: [
            tk("S 50-03", "In Pharmacy >> Billing tab >> show pending amount", "Pharmacy", "Sprint 50", ["Arun K"], "Divya R", "Sneha P", "Meena T", "Ravi Shankar", ["TC-0012", "TC-0013"]),
            tk("S 50-07", "Purchase Inward >> GRN Entry >> free quantity option", "Pharmacy", "Sprint 50", ["Karthik M"], "Divya R", "", "Meena T", "Ravi Shankar", ["TC-0021"]),
            tk("S 50-11", "Lab specimen collected time and ordered date", "Laboratory", "Sprint 50", ["Vignesh S"], "Priya N", "Sneha P", "Meena T", "Ravi Shankar", ["TC-0031", "TC-0032"]),
            tk("S 50-14", "Front Office >> patient search by mobile", "Front Office", "Sprint 50", ["Arun K", "Vignesh S"], "Priya N", "", "Meena T", "Ravi Shankar", ["TC-0042"]),
        ],
        executions: [ex("TC-0012", "pass", "Divya R", "2026-09-26"), ex("TC-0013", "not_run"), ex("TC-0014", "pass", "Divya R", "2026-09-26"),
            ex("TC-0021", "pass", "Divya R", "2026-09-27"), ex("TC-0031", "pass", "Priya N", "2026-09-27"),
            ex("TC-0032", "fail", "Priya N", "2026-09-27", "Print layout off by one line", "Cosmetic, fixed in 4.19"),
            ex("TC-0041", "pass", "Priya N", "2026-09-27"), ex("TC-0042", "pass", "Priya N", "2026-09-27"), ex("TC-0051", "pass", "Sneha P", "2026-09-28")],
    },
    {
        id: 3, version: "v-4.19.0", release_date: "2026-10-13", status: "in_regression", dev_bugs: 6,
        servers: [{ code: "prod", version: "v-4.19.0" }, { code: "default", version: "v-4.19.1" }],
        tickets: [
            tk("S 51-01", "Lab Report Print >> Culture Test >> efficacy ratio", "Laboratory", "Sprint 51", ["Karthik M"], "Priya N", "Sneha P", "Meena T", "Ravi Shankar", ["TC-0033"]),
            tk("S 51-04", "Purchase Request >> edit individual requests", "Pharmacy", "Sprint 51", ["Arun K"], "Divya R", "", "Meena T", "Ravi Shankar", ["TC-0022"]),
        ],
        executions: [ex("TC-0012", "pass", "Divya R", "2026-10-06"), ex("TC-0013", "fail", "Divya R", "2026-10-06", "Pending amount ignores partial return"),
            ex("TC-0014", "pass", "Divya R", "2026-10-06"), ex("TC-0021", "not_run"), ex("TC-0022", "pass", "Divya R", "2026-10-06"),
            ex("TC-0031", "pass", "Priya N", "2026-10-07"), ex("TC-0032", "not_run"), ex("TC-0033", "not_run"),
            ex("TC-0041", "blocked", "Priya N", "2026-10-07", "UAT login down"), ex("TC-0042", "not_run"), ex("TC-0051", "not_run")],
    },
];
const relById = (id) => RELEASES.find((r) => r.id === Number(id));

/* ── production bugs ────────────────────────────────────────────────────── */
let actSeq = 0;
const act = (type, description, owner, due_date, status = "Open", test_case = null) => {
    actSeq += 1;
    return { id: actSeq, type, description, owner, due_date, status, test_case };
};
const log = (stage, by, at) => ({ stage, by, at });

const BUGS = [
    {
        id: 14, code: "PB-00014", title: "Pharmacy bill shows wrong pending amount after a partial sales return",
        source: "customer_support", source_ref: "EMH-2026-0048", org_name: "Koyili Hospital", server_code: "prod",
        module: "Pharmacy", severity: "P1", stage: "classified", reported_at: "2026-10-05T10:42:00", reported_by: "Lakshmi (Support)",
        details: "After returning 2 of 5 items, the billing tab still shows the original total as pending. Staff collected extra cash from the patient.",
        release_id: null, ticket_code: null, untraced_reason: "", rca: null, actions: [], fix_ticket: null,
        stage_log: [log("raised", "Lakshmi (Support)", "2026-10-05T10:42:00"), log("classified", "Janarthanan V", "2026-10-05T11:10:00")],
    },
    {
        id: 13, code: "PB-00013", title: "500 error on GRN save when the scheme discount is 100%",
        source: "error_tracking", source_ref: "Error #812", org_name: "ANNAI Medical Centre", server_code: "prod",
        module: "Pharmacy", severity: "P2", stage: "traced", reported_at: "2026-10-03T16:05:00", reported_by: "Error Tracking",
        details: "ZeroDivisionError in GRN save when net rate is 0. 37 occurrences across 2 organizations.",
        release_id: 2, ticket_code: "S 50-07", untraced_reason: "", rca: null, actions: [], fix_ticket: null,
        stage_log: [log("raised", "Error Tracking", "2026-10-03T16:05:00"), log("classified", "Janarthanan V", "2026-10-03T17:00:00"), log("traced", "Janarthanan V", "2026-10-04T10:15:00")],
    },
    {
        id: 12, code: "PB-00012", title: "Lab result print missing ordered date for outsourced tests",
        source: "customer_support", source_ref: "EMH-2026-0044", org_name: "Gowsbai Hospital", server_code: "prod",
        module: "Laboratory", severity: "P2", stage: "fix_in_progress", reported_at: "2026-09-30T09:18:00", reported_by: "Ramesh (Support)",
        details: "Outsourced lab tests print a blank ordered date. In-house tests print correctly.",
        release_id: 2, ticket_code: "S 50-11", untraced_reason: "",
        rca: { missed_test_case: "TC-0032", category: "Test passed incorrectly", process_step: "Test design",
            why_not_detected: "TC-0032 covers in-house tests only. The outsourced flow is not in its steps.",
            root_cause: "Ordered date is read from the in-house order table only.",
            contributing_factors: "Outsourced flow was added in Sprint 48 without updating related test cases." },
        actions: [act("Corrective", "Read ordered date from the outsourced order when present", "Vignesh S", "2026-10-09", "In progress"),
            act("Preventive", "Add a test case: ordered date on outsourced lab print", "Priya N", "2026-10-10")],
        fix_ticket: { id: 9012, code: "S 50-11-BUG-01", status: "Dev In-Progress", status_id: 2, sprint: "Sprint 52", sprint_id: 52, project_id: 1, parent: "S 50-11" },
        stage_log: [log("raised", "Ramesh (Support)", "2026-09-30T09:18:00"), log("classified", "Janarthanan V", "2026-09-30T10:02:00"), log("traced", "Janarthanan V", "2026-09-30T12:40:00"),
            log("rca", "Priya N", "2026-10-01T15:20:00"), log("fix_in_progress", "Janarthanan V", "2026-10-02T09:30:00")],
    },
    {
        id: 11, code: "PB-00011", title: "Patient search by mobile returns family members without names",
        source: "internal", source_ref: "Raised by QA team", org_name: "FIMS", server_code: "prod",
        module: "Front Office", severity: "P3", stage: "closed", reported_at: "2026-09-18T14:30:00", reported_by: "Priya N",
        details: "Search returns one row per family member sharing a mobile number, and the name column is hidden.",
        release_id: 2, ticket_code: "S 50-14", untraced_reason: "",
        rca: { missed_test_case: "TC-0042", category: "Requirement gap", process_step: "Requirement",
            why_not_detected: "The requirement did not mention shared mobile numbers.", root_cause: "Results grouped by mobile instead of UHID.", contributing_factors: "" },
        actions: [act("Corrective", "Group results by UHID and show the name", "Arun K", "2026-09-22", "Done"),
            act("Preventive", "Add the shared-mobile case to TC-0042", "Priya N", "2026-09-23", "Done", "TC-0042")],
        fix_ticket: { id: 9011, code: "S 50-14-BUG-01", status: "Production", status_id: 8, sprint: "Sprint 52", sprint_id: 52, project_id: 1, parent: "S 50-14" },
        stage_log: [log("raised", "Priya N", "2026-09-18T14:30:00"), log("closed", "Janarthanan V", "2026-10-01T18:00:00")],
    },
    {
        id: 10, code: "PB-00010", title: "Admin could cancel a pharmacy bill after the configured window",
        source: "monitoring", source_ref: "Audit alert: bill cancel", org_name: "EVP Hospital", server_code: "prod",
        module: "Pharmacy", severity: "P0", stage: "rca", reported_at: "2026-09-12T11:02:00", reported_by: "Audit monitor",
        details: "Bill cancelled 3 days after creation although the window is 24 hours.",
        release_id: 1, ticket_code: "S 49-02", untraced_reason: "",
        rca: { missed_test_case: "TC-0014", category: "Missing test case", process_step: "Test design",
            why_not_detected: "TC-0014 only checks inside the window. Nobody tested just past the boundary.",
            root_cause: "Window compared against a UTC timestamp.", contributing_factors: "The production database clock runs in UTC." },
        actions: [act("Corrective", "Compare against the IST-converted bill time", "Karthik M", "2026-10-08")],
        fix_ticket: null,
        stage_log: [log("raised", "Audit monitor", "2026-09-12T11:02:00"), log("rca", "Divya R", "2026-09-15T16:45:00")],
    },
    {
        id: 9, code: "PB-00009", title: "Service master items missing from Radiology billing",
        source: "customer_support", source_ref: "EMH-2026-0039", org_name: "ARS Hospital", server_code: "prod",
        module: "Billing", severity: "P2", stage: "classified", reported_at: "2026-09-10T12:40:00", reported_by: "Lakshmi (Support)",
        details: "Radiology billing does not list newly added service master items.",
        release_id: null, ticket_code: null, untraced_reason: "", rca: null, actions: [], fix_ticket: null,
        stage_log: [log("raised", "Lakshmi (Support)", "2026-09-10T12:40:00"), log("classified", "Janarthanan V", "2026-09-10T14:00:00")],
    },
];
TEST_CASES.find((t) => t.code === "TC-0042").linked_bugs.push("PB-00011");
const bugById = (id) => BUGS.find((b) => b.id === Number(id));


/* ── production bug fields (same rules as quality/views.py _apply_bug_fields) ── */
let attSeq = 0;
const TEXT_FIELDS = ["title", "org_name", "module", "sub_module", "hospital_reporter", "details", "steps_to_reproduce",
    "expected_result", "actual_result", "app_version", "screen_url", "browser_device"];
const bugFields = (current, p, creating) => {
    const errors = [];
    const notes = [];
    const f = { ...current };
    const present = (k) => creating || k in p;
    TEXT_FIELDS.forEach((k) => { if (present(k)) f[k] = (p[k] || "").toString().trim(); });
    if (!f.title) errors.push("Add a summary.");
    if (!f.module) errors.push("Choose the module.");
    else if (present("sub_module") || present("module")) {
        const subs = SUBS[f.module] || [];
        if (subs.length && !subs.includes(f.sub_module)) {
            errors.push(f.sub_module ? `'${f.sub_module}' is not a sub-module of ${f.module}.` : "Choose the screen / sub-module.");
        }
    }
    if (creating || present("expected_result") || present("actual_result")) {
        if (!f.expected_result) errors.push("Say what should have happened (expected result).");
        if (!f.actual_result) errors.push("Say what actually happened (actual result).");
    }
    if (present("severity")) {
        if (!["P0", "P1", "P2", "P3"].includes(p.severity)) errors.push("Choose a severity.");
        else f.severity = p.severity;
    }
    if (present("occurred_at")) {
        const when = p.occurred_at ? new Date(p.occurred_at) : null;
        if (p.occurred_at && Number.isNaN(when.getTime())) errors.push("When it happened is not a valid date and time.");
        else if (when && when.getTime() > Date.now() + 5 * 60000) errors.push("When it happened cannot be in the future.");
        else f.occurred_at = when ? when.toISOString() : nowIso();
    }
    ["users_affected", "hospitals_affected"].forEach((k) => {
        if (!present(k)) return;
        const raw = p[k];
        if (raw === "" || raw == null) f[k] = null;
        else if (/^\d+$/.test(String(raw))) f[k] = Number(raw);
        else errors.push(`${k.replace("_", " ").replace(/^./, (c) => c.toUpperCase())} must be a whole number.`);
    });
    if (present("patient_safety")) f.patient_safety = !!p.patient_safety;
    if (f.patient_safety && (f.severity === "P2" || f.severity === "P3")) {
        f.severity = "P1";
        notes.push("Severity raised to P1 because patient safety or data integrity is affected.");
    }
    return { errors, fields: f, notes };
};

const MOCK_SPRINTS = [
    { id: 52, name: "Sprint 52", short_name: "S 52", is_live_support: true, project_id: 1 },
    { id: 53, name: "UAT Regression", short_name: "UAT", is_live_support: false, project_id: 1 },
];
const MOCK_STATUSES = ["To Do", "Dev In-Progress", "Dev Completed", "In QA", "QA Pass", "QA Fail", "UAT Pass", "Production"]
    .map((name, i) => ({ id: i + 1, name }));

const SOURCES_CS = [
    { ref: "EMH-2026-0051", title: "Culture report efficacy ratio column shifted", org_name: "Gowsbai Hospital", server_code: "prod",
      module: "Laboratory", severity: "P2", details: "Since yesterday, the efficacy ratio prints under the wrong antibiotic.",
      hospital_reporter: "Lab Technician", occurred_at: "2026-10-06T15:12:00", app_version: "v-4.18.5", browser_device: "Chrome 129",
      screen_url: "/laboratory/culture/print", status: "In Progress" },
    { ref: "EMH-2026-0048", title: "Pending amount wrong after partial return", org_name: "Koyili Hospital", server_code: "prod",
      module: "Pharmacy", severity: "P1", details: "Patient returned 2 strips. Bill still shows full amount pending.",
      hospital_reporter: "Pharmacy In-charge", occurred_at: "2026-10-05T10:31:00", app_version: "v-4.18.5", browser_device: "Edge",
      screen_url: "/pharmacy/billing", status: "Open" },
];
const SOURCES_ERR = [
    { ref: "Error #845", title: "GET /api/lab/result/print/ returned 500", org_name: "Gowsbai Hospital", server_code: "prod",
      module: "Laboratory", severity: "P2", details: "Antibiotic list and ratio list have different lengths.",
      hospital_reporter: "labuser01", occurred_at: "2026-10-06T15:05:00", screen_url: "/laboratory/culture/print", occurrences: 12 },
];

/* ── rules (mirror the planned backend) ─────────────────────────────────── */
const execIn = (rel, code) => (rel ? rel.executions.find((e) => e.tc_code === code) : null);
const ticketIn = (rel, code) => (rel ? rel.tickets.find((t) => t.code === code) : null);

const stats = (rel) => {
    const c = { pass: 0, fail: 0, blocked: 0, not_run: 0 };
    rel.executions.forEach((e) => { c[e.result] += 1; });
    const total = rel.executions.length;
    const escaped = BUGS.filter((b) => b.release_id === rel.id);
    const denom = escaped.length + rel.dev_bugs;
    return {
        total, ...c,
        coverage: total ? Math.round(((total - c.not_run) / total) * 100) : 0,
        escaped: escaped.length, dev_bugs: rel.dev_bugs,
        leakage: denom ? (escaped.length / denom) * 100 : 0,
    };
};
const gate = (rel) => {
    const notRun = rel.executions.filter((e) => e.result === "not_run").length;
    const unwaived = rel.executions.filter((e) => (e.result === "fail" || e.result === "blocked") && !e.waiver_reason.trim()).length;
    return { ok: !notRun && !unwaived, not_run: notRun, unwaived };
};
const lastResult = (code) => {
    let best = null;
    RELEASES.forEach((r) => {
        const e = execIn(r, code);
        if (e && e.result !== "not_run" && (!best || e.executed_at > best.executed_at)) {
            best = { result: e.result, version: r.version, executed_at: e.executed_at };
        }
    });
    return best;
};
const linkedTickets = (code) => {
    const out = [];
    RELEASES.forEach((r) => r.tickets.forEach((t) => { if (t.test_cases.includes(code) && !out.includes(t.code)) out.push(t.code); }));
    Object.entries(SPRINT_LINKS).forEach(([t, list]) => { if (list.includes(code) && !out.includes(t)) out.push(t); });
    return out;
};

const blockerFor = (b, next) => {
    if (next === "traced" && !b.release_id && !b.untraced_reason) return "Confirm the release and ticket on the Trace tab";
    if (next === "rca" && !b.rca) return "Save the root cause analysis";
    if (next === "fix_in_progress" && !b.fix_ticket) return "Create the fix ticket on the Fix tab";
    if (next === "regression" && !b.fix_ticket) return "There is no fix ticket any more. Create one on the Fix tab";
    if (next === "closed") {
        const prev = b.actions.filter((a) => a.type === "Preventive");
        if (!prev.length) return "Add at least one preventive action";
        if (prev.some((a) => !a.owner || !a.due_date)) return "Every preventive action needs an owner and due date";
    }
    return "";
};

/* ── serializers ────────────────────────────────────────────────────────── */
const serTc = (t) => ({ ...t, steps: t.steps.map((s) => ({ ...s })), linked_tickets: linkedTickets(t.code), last_result: lastResult(t.code) });

const serBug = (b, full = false) => {
    const rel = relById(b.release_id);
    const t = ticketIn(rel, b.ticket_code);
    const i = STAGE_KEYS.indexOf(b.stage);
    const next = i < 9 ? STAGE_KEYS[i + 1] : null;
    const base = {
        id: b.id, code: b.code, title: b.title, source: b.source, source_ref: b.source_ref,
        org_name: b.org_name, server_code: b.server_code, server_label: serverLabel(b.server_code),
        module: b.module, sub_module: b.sub_module || "", severity: b.severity, stage: b.stage,
        reported_at: b.reported_at, reported_by: b.reported_by, occurred_at: b.occurred_at || b.reported_at,
        patient_safety: !!b.patient_safety,
        release: rel ? { id: rel.id, version: rel.version, release_date: rel.release_date } : null,
        ticket_code: b.ticket_code, untraced_reason: b.untraced_reason,
    };
    if (!full) return base;
    const missedExec = b.rca && b.rca.missed_test_case ? execIn(rel, b.rca.missed_test_case) : null;
    return {
        ...base,
        details: b.details,
        hospital_reporter: b.hospital_reporter || "", steps_to_reproduce: b.steps_to_reproduce || "",
        expected_result: b.expected_result || "", actual_result: b.actual_result || "",
        app_version: b.app_version || "", screen_url: b.screen_url || "", browser_device: b.browser_device || "",
        users_affected: b.users_affected ?? null, hospitals_affected: b.hospitals_affected ?? null,
        attachments: (b.attachments || []).map(({ url, ...a }) => a),
        ticket: t ? {
            ...t,
            test_cases: t.test_cases.map((code) => {
                const e = execIn(rel, code);
                const c = tcByCode(code);
                return { code, title: c ? c.title : code, result: e ? e.result : null, tester: e ? e.tester : "", executed_at: e ? e.executed_at : "" };
            }),
        } : null,
        rca: b.rca ? {
            ...b.rca,
            missed_test_case_title: b.rca.missed_test_case ? (tcByCode(b.rca.missed_test_case) || {}).title : "",
            missed_execution: missedExec ? { result: missedExec.result, tester: missedExec.tester, executed_at: missedExec.executed_at } : null,
        } : null,
        actions: b.actions.map((a) => ({ ...a })),
        fix_ticket: b.fix_ticket,
        deleted_fix_ticket: b.deleted_fix_ticket || null,
        stage_log: b.stage_log.slice(),
        next_stage: next, next_stage_label: next ? STAGE_LABELS[i + 1] : null,
        next_blocker: next ? blockerFor(b, next) : "",
    };
};

const serRelease = (r, full = false) => {
    const base = {
        id: r.id, version: r.version, release_date: r.release_date, status: r.status,
        servers: r.servers.map((s) => ({ ...s, label: serverLabel(s.code) })),
        ticket_count: r.tickets.length, stats: stats(r),
    };
    if (!full) return base;
    return {
        ...base,
        gate: gate(r),
        modules: Array.from(new Set(r.tickets.map((t) => t.module))),
        tickets: r.tickets.map((t) => ({ ...t })),
        executions: r.executions.map((e) => {
            const c = tcByCode(e.tc_code) || {};
            return { ...e, test_case: { code: e.tc_code, title: c.title, module: c.module, critical: !!c.critical } };
        }),
        escaped_bugs: BUGS.filter((b) => b.release_id === r.id).map((b) => serBug(b)),
    };
};

/* ── plumbing ───────────────────────────────────────────────────────────── */
const ok = (data, extra = {}) => new Promise((res) => setTimeout(() => res({ status: "success", data, ...extra }), 120));
const fail = (message, extra = {}) => new Promise((_, rej) => setTimeout(() => {
    const e = new Error(message);
    e.payload = { status: "fail", message, ...extra };
    rej(e);
}, 120));
const logStage = (b) => b.stage_log.push(log(b.stage, ME, nowIso()));

const words = (t) => (t || "").toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 3);
const nextTcCode = () => `TC-${String(Math.max(...TEST_CASES.map((t) => Number(t.code.slice(3)))) + 1).padStart(4, "0")}`;

const validateTc = (p) => {
    const errors = [];
    const steps = (p.steps || []).filter((s) => (s.action || "").trim() || (s.expected || "").trim());
    if (!(p.title || "").trim()) errors.push("Add a title.");
    if (!p.sub_module) errors.push("Choose the screen / sub-module.");
    if (!(p.preconditions || "").trim()) errors.push("Add the preconditions (the setup needed before step 1).");
    if (!steps.length) errors.push("Add at least one step.");
    steps.forEach((s, i) => {
        if (!(s.action || "").trim()) errors.push(`Step ${i + 1} has an expected result but no action.`);
        if (!(s.expected || "").trim()) errors.push(`Step ${i + 1} needs an expected result.`);
    });
    if (p.applies_to === "setting" && !(p.setting_name || "").trim()) errors.push("Name the setting this case depends on.");
    return { errors, steps };
};

/* ── the API ────────────────────────────────────────────────────────────── */
export const qualityMock = {
    getMeta: () => ok({
        modules: Object.entries(SUBS).map(([name, sub_modules]) => ({ name, sub_modules })),
        members: MEMBERS.concat(ME !== "You" && !MEMBERS.includes(ME) ? [ME] : []),
        orgs: ORGS, servers: SERVERS,
        orgs_by_server: { prod: ORGS, default: ["eMedHub UAT Clinic"] },
        server_versions: { prod: "v-4.18.5", default: "v-4.18.6" },
        sprints: MOCK_SPRINTS,
        ticket_statuses: MOCK_STATUSES,
    }),

    getDashboard: () => {
        const open = BUGS.filter((b) => b.stage !== "closed");
        const byModule = {};
        BUGS.forEach((b) => { byModule[b.module] = (byModule[b.module] || 0) + 1; });
        const causes = {};
        BUGS.forEach((b) => { if (b.rca) causes[b.rca.category] = (causes[b.rca.category] || 0) + 1; });
        const deployed = RELEASES.filter((r) => r.status === "deployed" || r.status === "validated");
        const latest = deployed[deployed.length - 1];
        const current = RELEASES.find((r) => r.status === "in_regression");
        const actions = [];
        BUGS.forEach((b) => b.actions.forEach((a) => { if (a.status !== "Done") actions.push({ ...a, bug_id: b.id, bug_code: b.code }); }));
        return ok({
            totals: {
                bugs: BUGS.length, open: open.length, closed: BUGS.length - open.length,
                critical_open: open.filter((b) => b.severity === "P0" || b.severity === "P1").length,
            },
            latest_release: latest ? { version: latest.version, ...stats(latest) } : null,
            current_release: current ? { version: current.version, ...stats(current) } : null,
            by_module: Object.entries(byModule).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value),
            by_stage: STAGE_KEYS.slice(0, 9).map((k, i) => ({ key: k, label: STAGE_LABELS[i], value: open.filter((b) => b.stage === k).length })),
            root_causes: Object.entries(causes).map(([label, value]) => ({ label, value })),
            leakage_by_release: deployed.map((r) => ({ version: r.version, leakage: stats(r).leakage })),
            open_actions: actions,
        });
    },

    getBugs: (params = {}) => {
        const q = (params.q || "").toLowerCase();
        const list = BUGS.filter((b) =>
            (!params.severity || b.severity === params.severity) &&
            (!params.source || b.source === params.source) &&
            (!params.stage || (params.stage === "open" ? b.stage !== "closed" : b.stage === params.stage)) &&
            (!q || `${b.code} ${b.title} ${b.org_name} ${b.module} ${b.ticket_code || ""}`.toLowerCase().includes(q)));
        return ok(list.map((b) => serBug(b)));
    },
    getBug: (id) => (bugById(id) ? ok(serBug(bugById(id), true)) : fail("Production bug not found.")),

    createBug: (p) => {
        const { errors, fields, notes } = bugFields({}, p, true);
        if (errors.length) return fail("Fix these before logging the bug.", { errors });
        const id = Math.max(...BUGS.map((b) => b.id)) + 1;
        const b = {
            id, code: `PB-${String(id).padStart(5, "0")}`, source: p.source || "internal",
            source_ref: p.source_ref || `Raised by ${ME}`, server_code: p.server_code || "prod",
            ...fields, stage: "classified", reported_at: nowIso(), reported_by: ME,
            release_id: null, ticket_code: null, untraced_reason: "", rca: null, actions: [], fix_ticket: null, attachments: [],
            stage_log: [log("raised", ME, nowIso()), log("classified", ME, nowIso())],
        };
        BUGS.unshift(b);
        return ok(serBug(b, true), { message: [`${b.code} logged.`, ...notes].join(" ") });
    },

    deleteBug: (id) => {
        const i = BUGS.findIndex((b) => b.id === Number(id));
        if (i < 0) return fail("Production bug not found.");
        const b = BUGS[i];
        if (b.fix_ticket) return fail(`${b.code} still has fix ticket ${b.fix_ticket.code}. Delete that from the Fix tab first.`);
        BUGS.splice(i, 1);
        return ok({ id: b.id, code: b.code }, { message: `${b.code} deleted.` });
    },

    updateBug: (id, p) => {
        const b = bugById(id);
        const { errors, fields, notes } = bugFields(b, p, false);
        if (errors.length) return fail("Fix these before saving.", { errors });
        Object.assign(b, fields);
        return ok(serBug(b, true), { message: ["Saved.", ...notes].join(" ") });
    },

    searchSources: (type, q) => {
        const needle = (q || "").toLowerCase();
        const pool = type === "customer_support" ? SOURCES_CS : type === "error_tracking" ? SOURCES_ERR : null;
        if (!pool) return fail("type must be customer_support or error_tracking.");
        return ok(pool.filter((x) => !needle || `${x.ref} ${x.title} ${x.org_name}`.toLowerCase().includes(needle)));
    },

    // Files stay in the browser as object URLs; nothing leaves the page.
    uploadAttachments: (bugId, files) => {
        const b = bugById(bugId);
        b.attachments = b.attachments || [];
        Array.from(files).forEach((f) => {
            attSeq += 1;
            b.attachments.push({ id: attSeq, file_name: f.name, content_type: f.type, size_bytes: f.size,
                uploaded_by: ME, uploaded_at: nowIso(), url: URL.createObjectURL(f) });
        });
        return ok(serBug(b, true), { message: `${files.length} file(s) attached.` });
    },
    getAttachmentUrl: (attId) => {
        const a = BUGS.flatMap((b) => b.attachments || []).find((x) => x.id === Number(attId));
        return a ? ok({ url: a.url, file_name: a.file_name }) : fail("Attachment not found.");
    },
    deleteAttachment: (attId) => {
        const b = BUGS.find((x) => (x.attachments || []).some((a) => a.id === Number(attId)));
        if (!b) return fail("Attachment not found.");
        b.attachments = b.attachments.filter((a) => a.id !== Number(attId));
        return ok(serBug(b, true), { message: "File removed." });
    },

    advanceBug: (id) => {
        const b = bugById(id);
        const i = STAGE_KEYS.indexOf(b.stage);
        if (i >= 9) return fail("This bug is already closed.");
        const why = blockerFor(b, STAGE_KEYS[i + 1]);
        if (why) return fail(why);
        b.stage = STAGE_KEYS[i + 1];
        logStage(b);
        return ok(serBug(b, true), { message: `${b.code} moved to ${STAGE_LABELS[i + 1]}.` });
    },

    getSuggestions: (id) => {
        const b = bugById(id);
        const day = b.reported_at.slice(0, 10);
        const out = RELEASES
            .filter((r) => r.status === "deployed" && r.release_date <= day)
            .sort((a, c) => (a.release_date < c.release_date ? 1 : -1))
            .map((r) => ({
                release: { id: r.id, version: r.version, release_date: r.release_date, status: r.status },
                tickets: r.tickets.filter((t) => t.module === b.module).map((t) => ({ ...t })),
            }))
            .filter((x) => x.tickets.length);
        return ok(out);
    },

    traceBug: (id, p) => {
        const b = bugById(id);
        if (p.untraced_reason) {
            b.release_id = null; b.ticket_code = null; b.untraced_reason = p.untraced_reason;
        } else {
            const rel = relById(p.release_id);
            if (!rel || !ticketIn(rel, p.ticket_code)) return fail("That ticket is not part of the chosen release.");
            b.release_id = rel.id; b.ticket_code = p.ticket_code; b.untraced_reason = "";
        }
        if (STAGE_KEYS.indexOf(b.stage) < 2) { b.stage = "traced"; logStage(b); }
        return ok(serBug(b, true), { message: p.untraced_reason ? `Marked untraced: ${p.untraced_reason}.` : `Traced to ${relById(b.release_id).version} · ${b.ticket_code}.` });
    },
    clearTrace: (id) => {
        const b = bugById(id);
        b.release_id = null; b.ticket_code = null; b.untraced_reason = "";
        return ok(serBug(b, true));
    },

    saveRca: (id, p) => {
        const b = bugById(id);
        if (!b.release_id && !b.untraced_reason) return fail("Trace the bug before writing the root cause.");
        if (!p.category || !(p.root_cause || "").trim()) return fail("Choose a category and write the root cause.");
        b.rca = {
            missed_test_case: p.missed_test_case || "", category: p.category, process_step: p.process_step || "",
            why_not_detected: p.why_not_detected || "", root_cause: p.root_cause.trim(), contributing_factors: p.contributing_factors || "",
        };
        if (b.stage === "traced") { b.stage = "rca"; logStage(b); }
        return ok(serBug(b, true), { message: "Analysis saved." });
    },

    addAction: (id, p) => {
        const b = bugById(id);
        if (!(p.description || "").trim()) return fail("Describe the action first.");
        b.actions.push(act(p.type, p.description.trim(), p.owner || "", p.due_date || ""));
        return ok(serBug(b, true), { message: "Action added." });
    },

    createFixTicket: (id, sprintId) => {
        const b = bugById(id);
        if (!b.rca) return fail("Save the root cause analysis before creating the fix ticket.");
        if (b.fix_ticket) return fail(`Fix ticket ${b.fix_ticket.code} already exists.`);
        const sp = MOCK_SPRINTS.find((x) => x.id === Number(sprintId)) || MOCK_SPRINTS[0];
        b.deleted_fix_ticket = null;
        b.fix_ticket = {
            id: 9000 + b.id, code: b.ticket_code ? `${b.ticket_code}-BUG-01` : `${sp.short_name}-01`,
            status: "To Do", status_id: 1, sprint: sp.name, sprint_id: sp.id, project_id: sp.project_id,
            parent: b.ticket_code || "",
        };
        if (STAGE_KEYS.indexOf(b.stage) < 4) { b.stage = "fix_in_progress"; logStage(b); }
        return ok(serBug(b, true), { message: `Fix ticket ${b.fix_ticket.code} created in ${sp.name}.` });
    },

    deleteFixTicket: (id) => {
        const b = bugById(id);
        if (!b.fix_ticket) return fail("This bug has no fix ticket.");
        const code = b.fix_ticket.code;
        if (b.stage === "closed") return fail(`${code} is part of release v-4.18.5, so it is kept. Remove it from that release first.`);
        b.deleted_fix_ticket = code;
        b.fix_ticket = null;
        return ok(serBug(b, true), { message: `${code} deleted.` });
    },

    setFixTicketStatus: (id, statusId) => {
        const b = bugById(id);
        if (!b.fix_ticket) return fail("This bug has no fix ticket.");
        const st = MOCK_STATUSES.find((x) => x.id === Number(statusId));
        if (!st) return fail("Choose a ticket status.");
        if (st.name === "Production") return fail("This ticket cannot be moved to Production until its move-to-live request is approved.");
        b.fix_ticket = { ...b.fix_ticket, status: st.name, status_id: st.id };
        return ok(serBug(b, true), { message: `${b.fix_ticket.code} moved to ${st.name}.` });
    },

    getReleases: () => ok(RELEASES.slice().sort((a, b) => (a.release_date < b.release_date ? 1 : -1)).map((r) => serRelease(r))),
    getRelease: (id) => (relById(id) ? ok(serRelease(relById(id), true)) : fail("Release not found.")),

    updateExecution: (id, p) => {
        let rel = null;
        let e = null;
        RELEASES.forEach((r) => r.executions.forEach((x) => { if (x.id === Number(id)) { rel = r; e = x; } }));
        if (!e) return fail("Result not found.");
        if (rel.status !== "in_regression") return fail("This release's run is closed.");
        if (p.result !== undefined) {
            e.result = p.result;
            if (p.result === "not_run") { e.tester = ""; e.executed_at = ""; } else { e.tester = ME; e.executed_at = today(); }
        }
        if (p.waiver_reason !== undefined) e.waiver_reason = p.waiver_reason;
        return ok(serRelease(rel, true));
    },

    validateRelease: (id) => {
        const r = relById(id);
        const g = gate(r);
        if (!g.ok) {
            return fail("The release gate is closed.", {
                blockers: [g.not_run ? `${g.not_run} case(s) not run.` : "", g.unwaived ? `${g.unwaived} failed or blocked case(s) without a waiver.` : ""].filter(Boolean),
            });
        }
        r.status = "validated";
        return ok(serRelease(r, true), { message: `${r.version} validated. It can now be deployed.` });
    },

    getTestCases: (params = {}) => {
        const q = (params.q || "").toLowerCase();
        return ok(TEST_CASES.filter((t) =>
            (!params.module || t.module === params.module) &&
            (!params.case_type || t.case_type === params.case_type) &&
            (!params.status || t.status === params.status) &&
            (!q || `${t.code} ${t.title} ${t.sub_module} ${t.tags}`.toLowerCase().includes(q))).map(serTc));
    },

    similarTestCases: (title, exclude) => {
        const w = words(title);
        if (w.length < 2) return ok([]);
        return ok(TEST_CASES
            .filter((t) => t.code !== exclude)
            .map((t) => ({ t, n: w.filter((x) => words(`${t.title} ${t.sub_module}`).includes(x)).length }))
            .filter((x) => x.n >= 2).sort((a, b) => b.n - a.n).slice(0, 3)
            .map((x) => ({ id: x.t.id, code: x.t.code, title: x.t.title, sub_module: x.t.sub_module })));
    },

    createTestCase: (p) => {
        const { errors, steps } = validateTc(p);
        if (errors.length) return fail("Fix these before saving.", { errors });
        const code = nextTcCode();
        const t = {
            id: Math.max(...TEST_CASES.map((x) => x.id)) + 1, code,
            title: p.title.trim(), module: p.module, sub_module: p.sub_module, case_type: p.case_type, priority: p.priority,
            in_regression: !!p.in_regression, critical: !!p.critical, status: p.status, preconditions: p.preconditions.trim(),
            test_data: (p.test_data || "").trim(), applies_to: p.applies_to, setting_name: (p.setting_name || "").trim(),
            owner: p.owner, tags: (p.tags || "").trim(), steps, linked_bugs: [],
        };
        TEST_CASES.push(t);
        let message = `${code} added to the library`;
        if (p.ticket_id) {
            (TICKET_LINKS[p.ticket_id] || (TICKET_LINKS[p.ticket_id] = [])).push(code);
            message += " and linked to this ticket";
        } else if (p.ticket_code) {
            SPRINT_LINKS[p.ticket_code] = (SPRINT_LINKS[p.ticket_code] || []).concat(code);
            message += ` and linked to ${p.ticket_code}`;
        }
        if (p.bug_id) {
            const b = bugById(p.bug_id);
            const a = b && b.actions.find((x) => x.id === Number(p.action_id));
            t.linked_bugs.push(b.code);
            if (a) { a.test_case = code; a.status = "Done"; message += `. Preventive action for ${b.code} marked done`; }
        }
        return ok(serTc(t), { message: `${message}.` });
    },

    // Sprint ticket tab. Real ticket ids are unknown to the mock, so links are
    // kept per id and start empty.
    getTicketTestCases: (ticketId) => ok((TICKET_LINKS[ticketId] || []).map(tcByCode).filter(Boolean).map(serTc)),
    linkTicketTestCase: (ticketId, code) => {
        if (!tcByCode(code)) return fail("That test case is not in the library.");
        const list = TICKET_LINKS[ticketId] || (TICKET_LINKS[ticketId] = []);
        if (!list.includes(code)) list.push(code);
        return ok(list.map(tcByCode).map(serTc), { message: `${code} linked.` });
    },
    unlinkTicketTestCase: (ticketId, code) => {
        TICKET_LINKS[ticketId] = (TICKET_LINKS[ticketId] || []).filter((c) => c !== code);
        return ok(TICKET_LINKS[ticketId].map(tcByCode).map(serTc));
    },

    deleteTestCase: (id) => {
        const i = TEST_CASES.findIndex((x) => x.id === Number(id));
        if (i < 0) return fail("Test case not found.");
        const t = TEST_CASES[i];
        const ran = RELEASES.find((r) => r.executions.some((e) => e.tc_code === t.code && e.result !== "not_run"));
        if (ran) {
            return fail(`${t.code} has a recorded result in the ${ran.version} regression run, so it is kept as history. ` +
                "Mark it Deprecated to stop using it.", { code: "in_use" });
        }
        const bug = BUGS.find((b) => (b.rca && b.rca.missed_test_case === t.code) || b.actions.some((a) => a.test_case === t.code));
        if (bug) {
            return fail(`${t.code} is referenced by production bug ${bug.code}, so it is kept as history. ` +
                "Mark it Deprecated to stop using it.", { code: "in_use" });
        }
        TEST_CASES.splice(i, 1);
        RELEASES.forEach((r) => { r.executions = r.executions.filter((e) => e.tc_code !== t.code); });
        Object.keys(TICKET_LINKS).forEach((k) => { TICKET_LINKS[k] = TICKET_LINKS[k].filter((c) => c !== t.code); });
        Object.keys(SPRINT_LINKS).forEach((k) => { SPRINT_LINKS[k] = SPRINT_LINKS[k].filter((c) => c !== t.code); });
        return ok({ id: t.id, code: t.code }, { message: `${t.code} deleted.` });
    },

    updateTestCase: (id, p) => {
        const t = TEST_CASES.find((x) => x.id === Number(id));
        if (!t) return fail("Test case not found.");
        const { errors, steps } = validateTc({ ...t, ...p });
        if (errors.length) return fail("Fix these before saving.", { errors });
        Object.assign(t, p, { steps });
        return ok(serTc(t), { message: `${t.code} saved.` });
    },
};
